import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { TimePipe } from '@app/pipes/time.pipe';
import { DashboardFilterType } from '@app/reducers/page-filters/page-filters-dashboard.config';
import { selectAllowedDashboardFilter } from '@app/reducers/page-filters/page-filters-dashboard.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { selectAuthUserId } from '@reducers/auth/auth.selectors';
import { AppState } from '@reducers/interfaces';
import { ExchangeWithScheduleModel } from '@reducers/orm/exchange/exchange.model';
import { ExchangeService, getExchangesForAuthenticatedUserWithinPeriod } from '@reducers/orm/exchange/exchange.service';
import { PageFiltersService } from '@reducers/page-filters/page-filters.service';
import { mgcIconCalendarTimeAddCuteRe } from '@sb/mgc-icons/mgcIcon-calendar_time_add_cute_re.icon';
import { DashboardWidgetModule } from '@sb/ui/dashboard-widget/dashboard-widget.module';
import { IconRegistry } from '@sb/ui/icon';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { addWeeks } from 'date-fns';
import { finalize, forkJoin } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'my-exchange-widget',
  templateUrl: './my-exchange-widget.component.html',
  imports: [DashboardWidgetModule, TranslatePipe, LocaleDatePipe, TimePipe, RouterLink],
})
export class MyExchangeWidgetComponent implements OnInit {
  private store = inject<Store<AppState>>(Store);
  private exchangeService = inject(ExchangeService);
  private pageFilterService = inject(PageFiltersService);
  private iconRegistry = inject(IconRegistry);
  private translate = inject(TranslateService);

  periodOptions = [
    { label: this.translate.instant('week'), value: 1 },
    { label: this.translate.instant('2 weeks'), value: 2 },
    { label: this.translate.instant('3 weeks'), value: 3 },
    { label: this.translate.instant('4 weeks'), value: 4 },
  ];

  isLoading = signal(false);
  exchanges = computed<ExchangeWithScheduleModel[]>(() =>
    this.store.selectSignal(
      getExchangesForAuthenticatedUserWithinPeriod(
        toISODateString(new Date()),
        toISODateString(addWeeks(new Date(), this.widgetPeriod())),
      ),
    )(),
  );

  private filterType = DashboardFilterType.MY_EXCHANGES;
  private dashboardFilter = this.store.selectSignal(selectAllowedDashboardFilter(this.filterType));
  widgetPeriod = computed(() => this.dashboardFilter()?.widgetPeriod ?? 1);
  private userId = this.store.selectSignal(selectAuthUserId);

  constructor() {
    this.iconRegistry.registerIcons([mgcIconCalendarTimeAddCuteRe]);
  }

  ngOnInit() {
    this.isLoading.set(true);
    void forkJoin([this.exchangeService.loadIncoming(this.userId()), this.exchangeService.loadOutgoing(this.userId())])
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe();
  }

  onPeriodChange(period: number | string) {
    this.pageFilterService.setFilter('dashboard', this.filterType, {
      widgetPeriod: period,
    });
  }
}
